import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import { CONSTANTS } from "../../../../library/common/constant";
import CustomDialog from "../../../../library/common/CustomDialog";
import MiscellaneousListing from "../../../../library/component/MiscellaneousListing/MiscellaneousListing";
import styles from "./Sectors.module.scss";
import SearchBar from "../../../../library/common/SearchBar/SearchBar";
import { api } from "../../../../services";
import { throttle } from "lodash";

const {
  ALL_SECTORS_TITLE,
  ADD_NEW_SECTOR_BUTTON,
  SEARCH_SECTORS_LABEL,
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  CANCEL_BUTTON,
  ADD_BUTTON,
  THROTTLE_DURATION,
} = CONSTANTS;

const Sectors = () => {
  const sectors = useRef([]);
  const [filteredSectors, setFilteredSectors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [newSector, setnewSector] = useState({
    name: {
      en: "",
      hi: "",
    },
  });
  const [editingId, setEditingId] = useState(null);
  const [editedSector, setEditedSector] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const filterSectors = useCallback(() => {
    if (sectors.current?.length !== 0) {
      setFilteredSectors(
        sectors.current?.filter(
          (item) =>
            item?.name?.en
              ?.toLowerCase()
              ?.includes(searchText?.trim()?.toLowerCase()) ||
            item?.name?.hi?.includes(searchText?.trim())
        )
      );
    }
  }, [searchText]);

  const getSectors = useCallback(async () => {
    try {
      const { data } = await api.get("/sects");
      sectors.current = data;
      filterSectors();
    } catch (error) {
      console.error("Failed to fetch sectors", error);
    }
  }, [filterSectors]);

  useEffect(() => {
    getSectors();
  }, [getSectors]);

  useEffect(() => {
    filterSectors();
  }, [filterSectors]);

  const handleEditClicked = (id) => {
    const sector = sectors.current?.find((sect) => sect?._id === id);
    setEditingId(id);
    setEditedSector(sector);
  };

  const handleAddNewSector = () => {
    setModalOpen(true);
    setErrors({});
  };

  const handleOpenDeleteDialog = (item) => {
    setSectorToDelete(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSectorToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteSector = throttle(async () => {
    try {
      setIsLoading(true);
      const _id = sectorToDelete?._id;
      await api.delete("/sects/" + _id);
      sectors.current = sectors.current?.filter(
        (sector) => sector?._id !== _id
      );
      filterSectors();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleCloseModal = () => {
    setModalOpen(false);
    setErrors({});
    setnewSector({ name: { en: "", hi: "" } });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setnewSector((prev) => ({
      ...prev,
      name: { ...prev.name, [name]: value },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newSector?.name?.en?.trim())
      newErrors.name = { en: "Name in english is required" };
    if (!newSector?.name?.hi?.trim())
      newErrors.name = { hi: "Name in hindi is required" };
    return newErrors;
  };

  const handleAddSector = throttle(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        setIsLoading(true);
        await api.post("/sects", newSector);
        await getSectors();
        handleCloseModal();
      } catch (error) {
        console.error("Failed to post: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, THROTTLE_DURATION);

  const handleSaveClicked = throttle(async () => {
    try {
      setIsLoading(true);
      const id = editedSector?._id;
      await api.patch(`/sects/${id}`, {
        name: editedSector?.name,
      });
      sectors.current = sectors.current?.map((sect) =>
        sect?._id === id ? { ...sect, ...editedSector } : sect
      );
      setEditingId(null);
    } catch (error) {
      console.error("Failed to update: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEditedInputChange = (e) => {
    const { name, value } = e.target;
    setEditedSector((prev) => ({
      ...prev,
      name: { ...prev.name, [name]: value },
    }));
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  return (
    <Paper className={styles.paper_container}>
      <Typography
        variant="h5"
        className={styles.header_title}
        textAlign={"center"}
      >
        {ALL_SECTORS_TITLE}
      </Typography>

      {/* Add new button */}
      <div className={styles.search_add_container}>
        <Button variant="contained" onClick={handleAddNewSector}>
          {ADD_NEW_SECTOR_BUTTON}
        </Button>

        {/* Search bar */}
        <SearchBar
          placeholderText={SEARCH_SECTORS_LABEL}
          updateSearchText={handleSearch}
        />
      </div>

      {/* items */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name (En)</TableCell>
              <TableCell>Name (Hi)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSectors.map((item) => (
              <MiscellaneousListing
                key={`sector-${item?._id}`}
                isEditingItem={editingId === item?._id ? true : false}
                editedItem={editedSector}
                englishName={item?.name?.en}
                hindiName={item?.name?.hi}
                inputChange={handleEditedInputChange}
                saveClick={handleSaveClicked}
                editClick={() => handleEditClicked(item?._id)}
                deleteClick={() => handleOpenDeleteDialog(item)}
                disableDone={isLoading}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* add new dialog */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{ADD_NEW_SECTOR_BUTTON}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="English Name"
            name="en"
            value={newSector?.name?.en ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.en}
            helperText={errors?.name?.en}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Hindi Name"
            name="hi"
            value={newSector?.name?.hi ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.hi}
            helperText={errors?.name?.hi}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{CANCEL_BUTTON}</Button>
          <Button
            onClick={handleAddSector}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {ADD_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>

      {/* delete dialog */}
      <CustomDialog
        open={openDeleteDialog}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialog}
        onPrimaryClick={handleDeleteSector}
        onSecondaryClick={handleCloseDeleteDialog}
        disablePrimary={isLoading}
      />
    </Paper>
  );
};

export default Sectors;
